import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import "./sidebar.scss";
import { orderAppNav } from "./appNavLayout.js";
import { isDisplayableBlob } from "./profileImg.js";

export default function Sidebar({
  sidebarVisible,
  pinnedTabs,
  savePinnedTabs,
  getProfileImgThumb,
  items,
  pinnedItems,
  // Two optional render slots for app-specific chrome that is neither a menu
  // entry nor a pinned app: `menuTopSlot` goes above the first `items` entry,
  // `footerSlot` between the last delimiter and the user footer. Both are plain
  // nodes rendered as-is with no wrapper, so an app that passes neither gets
  // byte-identical markup to before they existed.
  menuTopSlot,
  footerSlot,
  user,
  logo,
  logoText,
  handleLogout,
  openProfile,
  appNavButtons = [],
}) {
  const [popover, setPopover] = useState(null);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [expandedParent, setExpandedParent] = useState(null);
  const [profileImgUrl, setProfileImgUrl] = useState(null);
  // Set once the browser has failed to decode the bytes behind profileImgUrl — see loadProfileImg.
  const [profileImgFailed, setProfileImgFailed] = useState(false);
  const navigate = useNavigate();

  // Load profile image.
  //
  // Guarded twice, because a thumbnail endpoint can answer 200 with a body that is NOT a decodable
  // image: a consumer API that proxies the picture from elsewhere may forward a "this user has no
  // picture" reply as an empty or JSON body while still labelling it image/jpeg. Creating an object URL
  // from that yields an <img> that fails to decode, and the browser shows its broken-image icon instead
  // of the initials this component already falls back to.
  //
  //   1. isDisplayableBlob rejects an empty or non-image-typed body before we make an object URL.
  //   2. the <img>'s onError clears it once the browser has actually failed to decode the bytes — the
  //      load-bearing one, since a JSON body mislabelled image/jpeg passes every check that can be
  //      made from the response alone.
  const loadProfileImg = useCallback(async (hrCode) => {
    try {
      const res = await getProfileImgThumb(hrCode);
      setProfileImgFailed(false);
      if (res.status === 200 && isDisplayableBlob(res.data)) {
        setProfileImgUrl(URL.createObjectURL(res.data));
      } else {
        // Clear it: a previous user may have left an avatar on screen, and rendering a stale URL for
        // the current one is its own bug.
        setProfileImgUrl(null);
      }
    } catch (error) {
      error;
    }
  }, []);

  // The bytes turned out not to be a renderable image. Drop the URL so the initials render, and revoke
  // it so the failed blob is not held onto.
  const onProfileImgError = () => {
    setProfileImgFailed(true);
    if (profileImgUrl) URL.revokeObjectURL(profileImgUrl);
  };

  useEffect(() => {
    if (user) {
      loadProfileImg(user._employee);
    }
  }, [user?._employee, user?.profileImg, loadProfileImg]);

  // Popover
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef();
  const closeTimeout = useRef();

  // Popover helpers
  const handlePopover = (type) => (e) => {
    clearTimeout(closeTimeout.current);
    setPopover(type);
    const rect = e.currentTarget.getBoundingClientRect();
    setPopoverPosition({
      top: rect.top,
      left: rect.right,
      height: rect.height,
    });
    triggerRef.current = e.currentTarget;
  };
  const handlePopoverClose = () =>
    (closeTimeout.current = setTimeout(() => setPopover(null), 100));
  const handlePopoverEnter = () => clearTimeout(closeTimeout.current);

  // Roles popover (account menu) — only used when the user has more than one role.
  const [rolesPopover, setRolesPopover] = useState(false);
  const [rolesPopoverPosition, setRolesPopoverPosition] = useState({
    top: 0,
    left: 0,
  });
  const rolesCloseTimeout = useRef();
  const openRoles = (e) => {
    clearTimeout(rolesCloseTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setRolesPopoverPosition({
      top: rect.top,
      left: rect.right,
      height: rect.height,
    });
    setRolesPopover(true);
  };
  const keepRolesOpen = () => clearTimeout(rolesCloseTimeout.current);
  const closeRoles = () =>
    (rolesCloseTimeout.current = setTimeout(() => setRolesPopover(false), 100));

  // "Switch App" dropdown — lists the other apps the user can open.
  const [appsPopover, setAppsPopover] = useState(false);
  const [appsPopoverPosition, setAppsPopoverPosition] = useState({
    top: 0,
    left: 0,
  });
  const appsTriggerRef = useRef();
  const appsPopoverRef = useRef();
  const orderedApps = orderAppNav(appNavButtons);
  const toggleApps = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setAppsPopoverPosition({
      top: rect.top,
      left: rect.left,
      height: rect.height,
    });
    setAppsPopover((open) => !open);
  };
  useEffect(() => {
    if (!appsPopover) return undefined;
    const onDocMouseDown = (e) => {
      if (
        appsTriggerRef.current?.contains(e.target) ||
        appsPopoverRef.current?.contains(e.target)
      )
        return;
      setAppsPopover(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [appsPopover]);

  // Pins
  const isPinned = (to) => pinnedTabs.includes(to);

  const togglePin = (to) => {
    if (isPinned(to)) {
      savePinnedTabs(pinnedTabs.filter((tab) => tab !== to));
    } else {
      savePinnedTabs([...pinnedTabs, to]);
    }
  };

  // In the collapsed (mobile) view the sidebar opens as a full-page overlay;
  // close it once the user actually navigates somewhere.
  const closeMobileSidebar = () => setMobileSidebar(false);

  const findEntryByTo = (entries, to) => {
    for (let entry of entries) {
      if (entry.to === to) return entry;
      if (entry.submenu) {
        const found = findEntryByTo(entry.submenu, to);
        if (found) return found;
      }
    }
    return null;
  };

  // Item
  const renderMenuItem = (entry) => {
    if (entry.show === false) return null;
    if (entry.submenu) {
      if (entry.submenu.every((item) => item.show === false)) return null;
      return (
        <div key={entry.label}>
          <SidebarParentButton
            icon={entry.icon}
            label={entry.label}
            isPinned={pinnedTabs.includes(entry.to)}
            onPin={(e) => {
              e.preventDefault();
              togglePin(entry.to);
            }}
            onExpand={(e) => {
              e.preventDefault();
              if (mobileSidebar) {
                setExpandedParent(
                  expandedParent === entry.label ? null : entry.label,
                );
              } else if (entry.submenu.length) {
                const firstVisible = entry.submenu.find(
                  (item) => item.show !== false,
                );
                if (firstVisible) navigate(firstVisible.to);
              }
            }}
            onMouseEnter={!mobileSidebar ? handlePopover(entry) : undefined}
            onMouseLeave={!mobileSidebar ? handlePopoverClose : undefined}
          />
          {mobileSidebar &&
            expandedParent === entry.label &&
            entry.show !== false && (
              <SidebarSubmenu
                items={entry.submenu}
                isPinned={(to) => pinnedTabs.includes(to)}
                onPin={togglePin}
                onNavigate={closeMobileSidebar}
                isActive={(to) => location.pathname === to}
              />
            )}
        </div>
      );
    }

    // Single item
    return (
      <NavLink
        key={entry.label}
        className={`sidebar-btn${location.pathname === entry.to ? " active" : ""}`}
        to={entry.to}
        onClick={closeMobileSidebar}
      >
        <i className="material-icons">{entry.icon}</i>
        <div className="sidebar-show">{entry.label}</div>
        <i
          className="material-icons right-btn"
          style={{ visibility: "hidden" }}
        >
          chevron_right
        </i>
        <i
          className={`material-icons pin-icon${pinnedTabs.includes(entry.to) ? " pinned" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePin(entry.to);
          }}
          title={pinnedTabs.includes(entry.to) ? "Unpin" : "Pin"}
        >
          push_pin
        </i>
      </NavLink>
    );
  };

  const resolvedPinnedTabs = pinnedTabs.filter((to) => findEntryByTo(items, to));

  return (
    <div
      className={classNames(
        "sidebar",
        sidebarVisible ? "show" : "hide",
        mobileSidebar && "show-mobile",
      )}
    >
      {" "}
      <div className="sidebar-logo">
        <button
          className="btn-sidebar-mobile"
          onClick={() => setMobileSidebar((prev) => !prev)}
          aria-label="Open sidebar"
        >
          <i className="material-icons">menu</i>
        </button>
        <NavLink to="/" onClick={closeMobileSidebar}>
          <div className="logo-wrapper">
            <img className="sidebar-logo-img" src={logo} alt="Logo" />
            <div className="logo-text">{logoText}</div>
          </div>
        </NavLink>
      </div>
      <div className="sidebar-delimiter"></div>
      {/* PINNED ITEMS */}
      {(pinnedItems?.length > 0 || resolvedPinnedTabs.length > 0) && (
        <div className="sidebar-pinned">
          <div className="sidebar-pinned-title">PINNED</div>

          {pinnedItems}
          {resolvedPinnedTabs.map((to) => renderMenuItem(findEntryByTo(items, to)))}
        </div>
      )}
      <div className="sidebar-delimiter"></div>
      <div className="sidebar-scrollable">
        <div className="sidebar-menu">
          {menuTopSlot}
          {items?.map((entry) => {
            return renderMenuItem(entry);
          })}
        </div>
      </div>
      {orderedApps.length > 0 && (
        <div className="sidebar-app-switch">
          <button
            type="button"
            ref={appsTriggerRef}
            className={`sidebar-app-switch-link${appsPopover ? " open" : ""}`}
            onClick={toggleApps}
          >
            <i className="material-icons">swap_horiz</i>
            <span className="sidebar-show">Switch App</span>
            <i className="material-icons sidebar-app-switch-caret">
              {appsPopover ? "expand_more" : "expand_less"}
            </i>
          </button>
        </div>
      )}
      <div className="sidebar-delimiter"></div>
      {footerSlot}
      <div className="sidebar-bottom">
        <div className="sidebar-bottom-left">
          <div className="user-badge" onClick={openProfile}>
            {profileImgUrl && !profileImgFailed ? (
              <img
                src={profileImgUrl}
                alt="avatar"
                className="user-badge-img"
                onError={onProfileImgError}
              />
            ) : (
              <>
                {user?.firstName?.charAt(0)}
                {user?.lastName?.charAt(0)}
              </>
            )}
          </div>
        </div>
        <div className="sidebar-bottom-right">
          <div className="sidebar-bottom-user" onClick={openProfile}>
            <div className="sidebar-bottom-name">
              {user?.firstName} {user?.lastName}
            </div>
            {user?.roles?.length > 0 && (
              <div
                className={`sidebar-bottom-role${user.roles.length > 1 ? " has-more" : ""}`}
                onMouseEnter={user.roles.length > 1 ? openRoles : undefined}
                onMouseLeave={user.roles.length > 1 ? closeRoles : undefined}
              >
                {user.roles[0].name}
                {user.roles.length > 1 ? ` +${user.roles.length - 1}` : ""}
              </div>
            )}
            <div className="sidebar-bottom-email">{user?.email}</div>
          </div>
          <NavLink
            className="sidebar-btn not-active"
            to="/"
            onClick={handleLogout}
          >
            <i className="material-icons">logout</i>
          </NavLink>
        </div>
      </div>
      {/* Popovers */}
      {popover && (
        <SidebarPopover
          show={popover}
          position={popoverPosition}
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverClose}
        >
          {popover?.submenu.map(
            (item) =>
              item.show !== false && (
                <NavLink
                  key={item.label}
                  className={`sidebar-btn${location.pathname === item.to ? " active" : ""}`}
                  to={item.to}
                >
                  <div className="sidebar-show">{item.label}</div>
                  <i
                    className={`material-icons pin-icon${pinnedTabs.includes(item.to) ? " pinned" : ""}`}
                    style={{ marginLeft: "auto", cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      togglePin(item.to);
                    }}
                    title={pinnedTabs.includes(item.to) ? "Unpin" : "Pin"}
                  >
                    push_pin
                  </i>
                </NavLink>
              ),
          )}
        </SidebarPopover>
      )}
      {rolesPopover && user?.roles?.length > 1 && (
        <SidebarPopover
          show={rolesPopover}
          position={rolesPopoverPosition}
          inverse
          onMouseEnter={keepRolesOpen}
          onMouseLeave={closeRoles}
        >
          <div className="sidebar-roles-popover">
            {user.roles.map((r) => (
              <div className="sidebar-role-item" key={r.id || r.name}>
                {r.name}
              </div>
            ))}
          </div>
        </SidebarPopover>
      )}
      {appsPopover && orderedApps.length > 0 && (
        <SidebarPopover
          show={appsPopover}
          position={appsPopoverPosition}
          inverse
          popoverRef={appsPopoverRef}
        >
          <div className="sidebar-app-switch-menu">
            {orderedApps.map((app) => (
              <a
                key={app.key}
                className="sidebar-app-switch-item"
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setAppsPopover(false)}
              >
                <span>{app.label}</span>
                <i className="material-icons">open_in_new</i>
              </a>
            ))}
          </div>
        </SidebarPopover>
      )}
    </div>
  );
}

function SidebarParentButton({
  icon,
  label,
  isPinned,
  onPin,
  onExpand,
  onMouseEnter,
  onMouseLeave,
}) {
  return (
    <div
      className="sidebar-btn"
      onClick={onExpand}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <i className="material-icons">{icon}</i>
      <div className="sidebar-show">{label}</div>
      <i className="material-icons right-btn" style={{ visibility: "visible" }}>
        chevron_right
      </i>
      <i
        className={`material-icons pin-icon${isPinned ? " pinned" : ""}`}
        style={{ cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          onPin(e);
        }}
        title={isPinned ? "Unpin" : "Pin"}
      >
        push_pin
      </i>
    </div>
  );
}

function SidebarSubmenu({ items, isPinned, onPin, onNavigate, isActive }) {
  const submenuRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(null);

  useEffect(() => {
    const updateMaxHeight = () => {
      if (!submenuRef.current) return;

      const rect = submenuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const margin = 12;
      const availableHeight = Math.max(120, viewportHeight - rect.top - margin);

      setMaxHeight(availableHeight);
    };

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    window.addEventListener("scroll", updateMaxHeight, true);

    return () => {
      window.removeEventListener("resize", updateMaxHeight);
      window.removeEventListener("scroll", updateMaxHeight, true);
    };
  }, [items.length]);

  return (
    <div
      className="sidebar-submenu"
      ref={submenuRef}
      style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}
    >
      {items.map(
        (item) =>
          item.show !== false && (
            <NavLink
              key={item.label}
              className={`sidebar-btn${isActive(item.to) ? " active" : ""}`}
              to={item.to}
              onClick={onNavigate}
            >
              <div className="sidebar-show">{item.label}</div>
              <i
                className={`material-icons pin-icon${isPinned(item.to) ? " pinned" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onPin(item.to);
                }}
                title={isPinned(item.to) ? "Unpin" : "Pin"}
              >
                push_pin
              </i>
            </NavLink>
          ),
      )}
    </div>
  );
}

function SidebarPopover({
  show,
  position,
  onMouseEnter,
  onMouseLeave,
  children,
  inverse = false,
  popoverRef,
}) {
  const [popoverHeight, setPopoverHeight] = useState(0);
  const localRef = useRef();
  const ref = popoverRef || localRef;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : popoverHeight;
  const viewportMargin = 12;

  useEffect(() => {
    if (ref.current) setPopoverHeight(ref.current.offsetHeight);
  }, [show, children, ref]);

  if (!show) return null;

  const naturalTop = inverse
    ? position.top - popoverHeight + position.height
    : position.top;
  const maxTop = Math.max(
    viewportMargin,
    viewportHeight - popoverHeight - viewportMargin,
  );
  const top = Math.min(Math.max(naturalTop, viewportMargin), maxTop);

  const style = {
    position: "fixed",
    top,
    left: position.left,
    zIndex: 100000,
    maxHeight: `${Math.max(120, viewportHeight - viewportMargin * 2)}px`,
    overflowY: "auto",
  };

  return ReactDOM.createPortal(
    <div
      className="sidebar-popover"
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={ref}
    >
      {children}
    </div>,
    document.body,
  );
}
